import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectSiteLayoutType,
  selectSiteActiveLayoutType,
} from '../../../common/redux/features/siteConfig/selectors';
import { com } from '../../../generated/proto-generated';

export const API_TYPES = com.wixpress.wixdocs.ViewerConfig.LayoutType;

interface layoutTypeResolverProps {
  children: any;
  matchInitialType?: boolean;
  showOnTypes?: string[];
  forceRender?: boolean;
  forceHide?: boolean;
}

const layoutTypeResolver = (showOnType) => ({
  children,
  matchInitialType,
  showOnTypes = [],
  forceRender,
  forceHide,
}: layoutTypeResolverProps) => {
  const initialLayoutType = useSelector(selectSiteLayoutType);
  const activeLayoutType = useSelector(selectSiteActiveLayoutType);

  throwIfNotRenderedInProvider(initialLayoutType);

  if (forceHide && initialLayoutType === API_TYPES.RPC_REST) {
    return null;
  }

  const shouldRenderChildren = useMemo(() => {
    const acceptableTypes = new Set(showOnTypes);

    if (forceRender && initialLayoutType === API_TYPES.RPC_REST) {
      return true;
    }

    if (matchInitialType) {
      return (
        showOnType === initialLayoutType ||
        acceptableTypes.has(initialLayoutType)
      );
    }

    return (
      showOnType === activeLayoutType || acceptableTypes.has(activeLayoutType)
    );
  }, [
    initialLayoutType,
    activeLayoutType,
    showOnTypes,
    forceRender,
    matchInitialType,
  ]);

  return <>{shouldRenderChildren ? children : null}</>;
};

// should be considered instead of the apiDisplay compound component
export const RestDisplay = layoutTypeResolver(API_TYPES.REST);
export const RpcDisplay = layoutTypeResolver(API_TYPES.RPC);
export const JsDisplay = layoutTypeResolver(API_TYPES.JS);
export const RpcRestDisplay = layoutTypeResolver(API_TYPES.RPC_REST);

export class ApiDisplay extends React.Component {
  static REST = RestDisplay;
  static RPC = RpcDisplay;
  static JS = JsDisplay;
  static RpcRest = RpcRestDisplay;

  render() {
    return this.props.children;
  }
}

function throwIfNotRenderedInProvider(type) {
  if (!type) {
    throw new Error(
      'ApiDisplay compound components must be rendered within the ApiDisplay component.',
    );
  }
}
